import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { NativeAppService } from '../shared/native-app.service';

const WEBVIEW_PARAMS = {
  IS_WEB_VIEW: 'is_web_view',
  SHOW_NAVBAR: 'show_navbar',
} as const;

/**
 * Service that automatically intercepts navigation in WebView context and preserves
 * WebView parameters (is_web_view, show_navbar) across route changes.
 *
 * This service listens to NavigationStart events to capture current URL parameters
 * and NavigationEnd events to apply those parameters to the new URL.
 * This ensures parameter preservation without forcing them on all WebView navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class WebViewNavigationInterceptorService {
  private isIntercepting = false;
  private currentWebViewParams: { isWebView?: string; showNavbar?: string } | null = null;

  constructor(private router: Router, private location: Location, private nativeAppService: NativeAppService) {
    this.initializeService();
  }

  /**
   * Initializes the service by setting up navigation listeners and capturing
   * any existing WebView parameters from the current URL.
   */
  private initializeService(): void {
    this.captureCurrentWebViewParams();

    // Listen to navigation start events to capture current URL parameters
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => {
      this.handleNavigationStart();
    });

    // Listen to navigation end events to apply captured parameters
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.handleNavigationEnd(event as NavigationEnd);
    });
  }

  /**
   * Captures WebView parameters from the current window location.
   * This handles cases where the service starts on a page that already has WebView parameters.
   */
  private captureCurrentWebViewParams(): void {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(WEBVIEW_PARAMS.IS_WEB_VIEW) || urlParams.has(WEBVIEW_PARAMS.SHOW_NAVBAR)) {
      this.currentWebViewParams = {
        isWebView: urlParams.get(WEBVIEW_PARAMS.IS_WEB_VIEW) || undefined,
        showNavbar: urlParams.get(WEBVIEW_PARAMS.SHOW_NAVBAR) || undefined,
      };
    }
  }

  private handleNavigationStart(): void {
    const isWebView = this.nativeAppService.isWebView();
    if (!isWebView) {
      return;
    }

    this.captureCurrentWebViewParams();
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    if (this.isIntercepting) {
      return;
    }

    const isWebView = this.nativeAppService.isWebView();
    if (!isWebView) {
      return;
    }

    if (!this.currentWebViewParams) {
      return;
    }

    const currentUrl = event.url;
    const urlWithParams = this.addWebViewParamsToUrl(currentUrl, this.currentWebViewParams);

    if (currentUrl !== urlWithParams) {
      this.isIntercepting = true;

      this.location.replaceState(urlWithParams);

      setTimeout(() => {
        this.isIntercepting = false;
      }, 100);
    }
  }

  /**
   * Adds WebView parameters to a URL if they were captured from the previous navigation.
   * Only preserves parameters that were present in the previous URL.
   *
   * @param url - The target URL to add parameters to
   * @param webViewParams - The captured WebView parameters from the previous URL
   * @returns The URL with WebView parameters added if they existed previously
   */
  private addWebViewParamsToUrl(url: string, webViewParams: { isWebView?: string; showNavbar?: string }): string {
    const [path, queryString] = url.split('?');
    const params = new URLSearchParams(queryString || '');

    if (webViewParams.isWebView && !params.has(WEBVIEW_PARAMS.IS_WEB_VIEW)) {
      params.set(WEBVIEW_PARAMS.IS_WEB_VIEW, webViewParams.isWebView);
    }

    if (webViewParams.showNavbar && !params.has(WEBVIEW_PARAMS.SHOW_NAVBAR)) {
      params.set(WEBVIEW_PARAMS.SHOW_NAVBAR, webViewParams.showNavbar);
    }

    const newQueryString = params.toString();
    return newQueryString ? `${path}?${newQueryString}` : path;
  }
}
